import React, { useState, useRef, ChangeEvent, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../config/constants';
import BookMetadataService, { BookMetadata } from '../../services/BookMetadataService';
import {
  ExternalBookSearchResult,
  BulkImportJob,
  importExternalBook,
  startBulkExternalImport,
  getBulkExternalImport,
  searchExternalBooks
} from '../../services/ExternalImportService';

// Interfață pentru datele unei cărți
interface BookData {
  title: string;
  author: string;
  description: string;
  category: string;
  publishedYear: string;
  file: File | null;
  isbn?: string;
}

// Interfață pentru starea încărcării multiple
interface MultipleUploadBook {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message: string;
  id?: number;
}

interface Category {
  category: string;
  count: number;
}

type ExternalSourceFilter = 'all' | 'wikisource' | 'gutenberg';

const AddBook: React.FC = () => {
  const { token, isAdmin } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const multipleFileInputRef = useRef<HTMLInputElement>(null);
  const coverImageInputRef = useRef<HTMLInputElement>(null);

  // State pentru upload de o singură carte
  const [bookData, setBookData] = useState<BookData>({
    title: '',
    author: '',
    description: '',
    category: '',
    publishedYear: '',
    file: null,
  });

  // State pentru upload multiplu
  const [isMultipleUpload, setIsMultipleUpload] = useState(false);
  const [multipleBooks, setMultipleBooks] = useState<MultipleUploadBook[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // State pentru încărcarea imaginii de copertă
  const [fileData, setFileData] = useState<File | null>(null);
  const [coverImageData, setCoverImageData] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  // State pentru ISBN și preluarea metadatelor
  const [isbn, setIsbn] = useState('');
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState('');
  const [metadataSuccess, setMetadataSuccess] = useState('');

  // Genurile de cărți predefinite
  const genres = [
    'Ficțiune',
    'Non-ficțiune',
    'Mister',
    'Thriller',
    'Science Fiction',
    'Fantasy',
    'Romance',
    'Western',
    'Distopie',
    'Contemporan',
    'Istoric',
    'Poezie',
    'Dramă',
    'Copii',
    'Young Adult',
    'Biografii',
    'Autobiografii',
    'Eseuri',
    'Tehnică',
    'Dicționare și enciclopedii',
    'Altele'
  ];

  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [externalQuery, setExternalQuery] = useState('');
  const [externalSource, setExternalSource] = useState<ExternalSourceFilter>('all');
  const [externalResults, setExternalResults] = useState<ExternalBookSearchResult[]>([]);
  const [isSearchingExternal, setIsSearchingExternal] = useState(false);
  const [externalError, setExternalError] = useState('');
  const [importingExternalId, setImportingExternalId] = useState<string | null>(null);
  const [bulkImportSource, setBulkImportSource] = useState<'wikisource' | 'gutenberg'>('wikisource');
  const [bulkImportQuery, setBulkImportQuery] = useState('');
  const [bulkImportLimit, setBulkImportLimit] = useState('1500');
  const [bulkImportJob, setBulkImportJob] = useState<BulkImportJob | null>(null);
  const [isStartingBulkImport, setIsStartingBulkImport] = useState(false);

  // Verifică dacă utilizatorul este admin la încărcarea componentei
  React.useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/');
      return;
    }
  }, [token, isAdmin, navigate]);

  // Încarcă categoriile din backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/books/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Eroare la încărcarea categoriilor:', error);
        // Fallback la categoriile predefinite
        const fallbackCategories = genres.map(genre => ({
          category: genre,
          count: 0
        }));
        setCategories(fallbackCategories);
      }
    };
    fetchCategories();
  }, []);

  // Handler pentru schimbarea input-urilor text
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBookData(prev => ({ ...prev, [name]: value }));
  };

  // Handler pentru schimbarea fișierului (upload singular)
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setBookData(prev => ({ ...prev, file: e.target.files![0] }));
    }
  };

  // Handler pentru schimbarea fișierelor (upload multiplu)
  const handleMultipleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const newBooks: MultipleUploadBook[] = filesArray.map(file => ({
        file,
        progress: 0,
        status: 'pending',
        message: 'În așteptare'
      }));
      
      setMultipleBooks(newBooks);
    }
  };

  // Procesează schimbarea fișierului de copertă
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Verifică tipul fișierului (doar imagini)
      if (!file.type.match('image.*')) {
        alert('Vă rugăm să selectați un fișier de imagine.');
        return;
      }
      
      setCoverImageData(file);
      
      // Crează o previzualizare a imaginii
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        if (loadEvent.target && loadEvent.target.result) {
          setCoverImagePreview(loadEvent.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler pentru schimbarea ISBN-ului
  const handleIsbnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsbn(e.target.value);
  };

  // Funcție pentru preluarea metadatelor cărții după ISBN
  const fetchBookMetadataByISBN = async () => {
    if (!isbn || isbn.trim() === '') {
      setMetadataError('Introduceți un ISBN valid pentru a prelua informațiile cărții');
      return;
    }

    setIsLoadingMetadata(true);
    setMetadataError('');
    setMetadataSuccess('');

    try {
      const bookMetadata = await BookMetadataService.searchByISBN(isbn);
      
      if (!bookMetadata) {
        setMetadataError(`Nu s-a găsit nicio carte cu ISBN-ul ${isbn}`);
        return;
      }
      
      updateBookDataFromMetadata(bookMetadata);
      setMetadataSuccess('Informațiile cărții au fost preluate cu succes!');
      
      // Încărcăm și imaginea copertei dacă există
      if (bookMetadata.coverImageUrl) {
        await downloadCoverImage(bookMetadata.coverImageUrl);
      }
    } catch (error) {
      console.error('Eroare la preluarea metadatelor cărții:', error);
      setMetadataError('Eroare la preluarea informațiilor cărții. Verificați ISBN-ul și încercați din nou.');
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  // Funcție pentru preluarea metadatelor cărții după titlu și autor
  const fetchBookMetadataByTitleAndAuthor = async () => {
    if (!bookData.title || bookData.title.trim() === '') {
      setMetadataError('Introduceți titlul cărții pentru a prelua informații');
      return;
    }

    setIsLoadingMetadata(true);
    setMetadataError('');
    setMetadataSuccess('');

    try {
      const bookMetadata = await BookMetadataService.searchByTitleAndAuthor(
        bookData.title,
        bookData.author
      );
      
      if (!bookMetadata) {
        setMetadataError(`Nu s-a găsit nicio carte cu titlul "${bookData.title}" ${bookData.author ? `și autorul "${bookData.author}"` : ''}`);
        return;
      }
      
      updateBookDataFromMetadata(bookMetadata);
      setMetadataSuccess('Informațiile cărții au fost preluate cu succes!');
      
      // Încărcăm și imaginea copertei dacă există
      if (bookMetadata.coverImageUrl) {
        await downloadCoverImage(bookMetadata.coverImageUrl);
      }
    } catch (error) {
      console.error('Eroare la preluarea metadatelor cărții:', error);
      setMetadataError('Eroare la preluarea informațiilor cărții. Verificați titlul și autorul, apoi încercați din nou.');
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  // Actualizează datele cărții cu metadatele preluate
  const updateBookDataFromMetadata = (metadata: BookMetadata) => {
    setBookData(prev => ({
      ...prev,
      title: metadata.title || prev.title,
      author: metadata.author || prev.author,
      description: metadata.description || prev.description,
      category: metadata.category || prev.category,
      publishedYear: metadata.publishedYear || prev.publishedYear,
      isbn: metadata.isbn
    }));
  };

  // Descarcă și setează imaginea copertei
  const downloadCoverImage = async (imageUrl: string) => {
    try {
      const imageBlob = await BookMetadataService.downloadCoverImage(imageUrl);
      
      if (!imageBlob) {
        console.warn('Nu s-a putut descărca imaginea copertei');
        return;
      }
      
      const fileName = `cover_${Date.now()}.jpg`;
      const coverFile = BookMetadataService.createFileFromBlob(imageBlob, fileName);
      
      setCoverImageData(coverFile);
      
      // Creează o previzualizare a imaginii
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        if (loadEvent.target && loadEvent.target.result) {
          setCoverImagePreview(loadEvent.target.result as string);
        }
      };
      reader.readAsDataURL(coverFile);
      
    } catch (error) {
      console.error('Eroare la descărcarea imaginii copertei:', error);
    }
  };

  // Funcție pentru încărcarea unei singure cărți
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    if (isMultipleUpload) {
      // Apelăm funcția de upload multiplu
      await uploadMultipleBooks();
      return;
    }
    
    // Validare pentru upload singular
    if (!bookData.title.trim() || !bookData.author.trim() || !bookData.file) {
      setErrorMessage('Titlul, autorul și fișierul sunt obligatorii!');
      return;
    }

    try {
      setIsUploading(true);
      
      const formData = new FormData();
      formData.append('title', bookData.title);
      formData.append('author', bookData.author);
      formData.append('description', bookData.description);
      formData.append('category', bookData.category);
      formData.append('publishedYear', bookData.publishedYear);
      
      // Adaugă ISBN-ul dacă există
      if (bookData.isbn) {
        formData.append('isbn', bookData.isbn);
      }
      
      if (bookData.file) {
        formData.append('file', bookData.file);
      }

      // Adaugă imaginea de copertă dacă există
      if (coverImageData) {
        formData.append('coverImage', coverImageData);
      }

      const response = await fetch(`${API_URL}/books`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Eroare server: ${response.status}`);
      }

      const result = await response.json();
      setSuccessMessage(`Carte adăugată cu succes: ${result.book.title}`);
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('Eroare la încărcarea cărții:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Eroare la încărcarea cărții');
    } finally {
      setIsUploading(false);
    }
  };

  // Funcție pentru încărcarea multiplă de cărți
  const uploadMultipleBooks = async () => {
    if (multipleBooks.length === 0) {
      setErrorMessage('Nu ați selectat fișiere pentru încărcare!');
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Creăm formData pentru multiple fișiere
      const formData = new FormData();
      
      // Adăugăm fiecare fișier în formData
      multipleBooks.forEach((book, index) => {
        formData.append('files', book.file);
      });
      
      // Adăugăm informații generale (opțional)
      if (bookData.author) formData.append('author', bookData.author);
      if (bookData.category) formData.append('category', bookData.category);

      // Adaugă imaginea de copertă dacă există
      if (coverImageData) {
        formData.append('coverImage', coverImageData);
      }

      // Actualizăm starea pentru toate cărțile la "uploading"
      setMultipleBooks(prev => 
        prev.map(book => ({ ...book, status: 'uploading', message: 'Se încarcă...' }))
      );

      // Facem cererea de upload multiplu
      const response = await fetch(`${API_URL}/books/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Eroare server: ${response.status}`);
      }

      const result = await response.json();
      
      // Actualizăm starea pentru fiecare carte în funcție de rezultat
      const updatedBooks = [...multipleBooks];
      result.results.forEach((res: any, index: number) => {
        if (index < updatedBooks.length) {
          if (res.success) {
            updatedBooks[index] = {
              ...updatedBooks[index],
              status: 'success',
              message: 'Încărcat cu succes',
              id: res.bookId
            };
          } else {
            updatedBooks[index] = {
              ...updatedBooks[index],
              status: 'error',
              message: res.error || 'Eroare la încărcare'
            };
          }
        }
      });
      
      setMultipleBooks(updatedBooks);
      
      // Verificăm câte au fost încărcate cu succes
      const successCount = result.results.filter((res: any) => res.success).length;
      if (successCount > 0) {
        setSuccessMessage(`${successCount} cărți au fost încărcate cu succes`);
        
        // Reset form pentru fișiere noi
        if (multipleFileInputRef.current) {
          multipleFileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Eroare la încărcarea multiplă:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Eroare la încărcarea multiplă a cărților');
      
      // Actualizăm starea tuturor cărților la "error"
      setMultipleBooks(prev => 
        prev.map(book => ({ 
          ...book, 
          status: 'error', 
          message: 'Eroare la încărcare' 
        }))
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Funcție pentru a reseta formularul
  const resetForm = () => {
    if (isMultipleUpload) {
      setMultipleBooks([]);
      if (multipleFileInputRef.current) {
        multipleFileInputRef.current.value = '';
      }
    } else {
      setBookData({
        title: '',
        author: '',
        description: '',
        category: '',
        publishedYear: '',
        file: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Resetează ISBN-ul și alte câmpuri adăugate
      setIsbn('');
      setMetadataError('');
      setMetadataSuccess('');
    }
    
    // Resetează imaginea de copertă
    setCoverImageData(null);
    setCoverImagePreview(null);
    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = '';
    }
    
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleCategorySelect = (category: string) => {
    if (category === 'add-new') {
      setShowNewCategoryInput(true);
      return;
    }
    setBookData(prev => ({ ...prev, category }));
  };

  const handleAddNewCategory = () => {
    if (newCategory.trim()) {
      const newCat = {
        category: newCategory.trim(),
        count: 0
      };
      setCategories([...categories, newCat]);
      setBookData(prev => ({ ...prev, category: newCategory.trim() }));
      setNewCategory('');
      setShowNewCategoryInput(false);
    }
  };

  const handleExternalSearch = async () => {
    if (!token) {
      setExternalError('Trebuie să fii autentificat pentru a căuta în surse externe.');
      return;
    }

    if (!externalQuery.trim()) {
      setExternalError('Introdu un titlu sau un autor pentru căutare.');
      return;
    }

    setIsSearchingExternal(true);
    setExternalError('');

    try {
      const results = await searchExternalBooks(externalQuery, externalSource, token);
      setExternalResults(results);

      if (results.length === 0) {
        setExternalError('Nu am găsit rezultate în Wikisource sau Project Gutenberg.');
      }
    } catch (error) {
      console.error('Eroare la căutarea externă:', error);
      setExternalResults([]);
      setExternalError('A apărut o eroare la căutarea în sursele externe.');
    } finally {
      setIsSearchingExternal(false);
    }
  };

  const handleExternalImport = async (result: ExternalBookSearchResult) => {
    if (!token) {
      setExternalError('Trebuie să fii autentificat pentru a importa o carte.');
      return;
    }

    setImportingExternalId(`${result.source}:${result.externalId}`);
    setExternalError('');
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await importExternalBook(result.source, result.externalId, token);
      setSuccessMessage(`Carte adăugată cu succes: ${response.book.title}`);
    } catch (error: any) {
      console.error('Eroare la importul extern:', error);
      setExternalError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Importul a eșuat.'
      );
    } finally {
      setImportingExternalId(null);
    }
  };

  const handleStartBulkImport = async () => {
    if (!token || !bulkImportQuery.trim()) return;
    setIsStartingBulkImport(true);
    setExternalError('');
    try {
      const job = await startBulkExternalImport(
        bulkImportSource,
        bulkImportQuery,
        Math.min(Math.max(Number(bulkImportLimit) || 1500, 1), 1500),
        token
      );
      setBulkImportJob(job);
    } catch (error: any) {
      setExternalError(error?.response?.data?.message || 'Nu s-a putut porni importul în masă.');
    } finally {
      setIsStartingBulkImport(false);
    }
  };

  useEffect(() => {
    if (!bulkImportJob || !token || ['completed', 'failed'].includes(bulkImportJob.status)) return;
    const interval = window.setInterval(async () => {
      try {
        const updatedJob = await getBulkExternalImport(bulkImportJob.id, token);
        setBulkImportJob(updatedJob);
        if (['completed', 'failed'].includes(updatedJob.status)) window.clearInterval(interval);
      } catch (error) {
        console.error('Eroare la verificarea importului în masă:', error);
      }
    }, 2500);
    return () => window.clearInterval(interval);
  }, [bulkImportJob?.id, bulkImportJob?.status, token]);

  // Handler pentru încărcarea multiplă
  const handleMultipleUpload = async () => {
    if (multipleBooks.length === 0) {
      setErrorMessage(t('admin.fillRequiredFields'));
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Actualizează status-ul la "uploading" pentru toate fișierele
      setMultipleBooks(prev => 
        prev.map(book => ({ 
          ...book, 
          status: 'uploading',
          message: t('admin.uploading')
        }))
      );

      let successCount = 0;
      let errorCount = 0;

      // Procesează fiecare fișier individual
      for (let i = 0; i < multipleBooks.length; i++) {
        const book = multipleBooks[i];
        const formData = new FormData();
        
        // Adaugă fișierul
        formData.append('file', book.file);
        
        // Adaugă metadatele comune din bookData
        formData.append('title', book.file.name.replace(/\.[^/.]+$/, ''));
        formData.append('author', bookData.author || '');
        formData.append('category', bookData.category || '');
        formData.append('description', bookData.description || '');
        formData.append('publishedYear', bookData.publishedYear || '');

        try {
          const response = await fetch(`${API_URL}/books`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (response.ok) {
            // Actualizează status-ul ca "success"
            setMultipleBooks(prev => 
              prev.map((b, index) => index === i ? { 
                ...b, 
                status: 'success',
                message: t('admin.uploadSuccess')
              } : b)
            );
            successCount++;
          } else {
            // Actualizează status-ul ca "error"
            setMultipleBooks(prev => 
              prev.map((b, index) => index === i ? { 
                ...b, 
                status: 'error',
                message: t('admin.uploadError')
              } : b)
            );
            errorCount++;
          }
        } catch (error) {
          // Actualizează status-ul ca "error" pentru eroarea de rețea
          setMultipleBooks(prev => 
            prev.map((b, index) => index === i ? { 
              ...b, 
              status: 'error',
              message: t('admin.uploadError')
            } : b)
          );
          errorCount++;
        }
      }

      // Afișează mesajul final
      if (successCount > 0 && errorCount === 0) {
        setSuccessMessage(`${successCount} cărți au fost adăugate cu succes!`);
      } else if (successCount > 0 && errorCount > 0) {
        setSuccessMessage(`${successCount} cărți au fost adăugate cu succes. ${errorCount} au eșuat.`);
      } else {
        setErrorMessage(`Toate încărcările au eșuat. Vă rugăm să încercați din nou.`);
      }
    } catch (error) {
      console.error('Eroare la încărcarea multiplă:', error);
      setErrorMessage(t('admin.uploadError'));
      
      // Actualizează toate status-urile ca "error"
      setMultipleBooks(prev => 
        prev.map(book => ({ 
          ...book, 
          status: 'error', 
          message: t('admin.uploadError')
        }))
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white overflow-hidden shadow-lg rounded-lg">
            <div className="px-6 py-8 sm:p-8">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">{t('admin.addBook')}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {isMultipleUpload 
                      ? t('admin.uploadMultiple')
                      : t('admin.uploadSingle')
                    }
                  </p>
                            </div>
                
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="mb-8 rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <h4 className="text-base font-semibold text-gray-900">Import rapid din surse externe</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Caută în Wikisource România și Project Gutenberg, apoi adaugă cartea direct în biblioteca locală.
                    </p>

                    <div className="mt-4 grid gap-3 sm:grid-cols-4">
                      <input
                        type="text"
                        value={externalQuery}
                        onChange={(e) => setExternalQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleExternalSearch();
                          }
                        }}
                        placeholder="Titlu sau autor"
                        className="sm:col-span-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                      <select
                        value={externalSource}
                        onChange={(e) => setExternalSource(e.target.value as ExternalSourceFilter)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="all">Toate sursele</option>
                        <option value="wikisource">Wikisource România</option>
                        <option value="gutenberg">Project Gutenberg</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleExternalSearch}
                        disabled={isSearchingExternal}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSearchingExternal ? 'Se caută...' : 'Caută'}
                      </button>
                    </div>

                    <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 p-4">
                      <h5 className="text-sm font-semibold text-gray-900">Importă automat până la 1.500 de volume</h5>
                      <p className="mt-1 text-xs text-gray-600">
                        Introdu un criteriu de căutare. Importul rulează în fundal și sare peste duplicate.
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-4">
                        <input
                          type="text"
                          value={bulkImportQuery}
                          onChange={(e) => setBulkImportQuery(e.target.value)}
                          placeholder="ex.: literatură română"
                          className="sm:col-span-2 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        />
                        <select
                          value={bulkImportSource}
                          onChange={(e) => setBulkImportSource(e.target.value as 'wikisource' | 'gutenberg')}
                          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        >
                          <option value="wikisource">Wikisource România</option>
                          <option value="gutenberg">Project Gutenberg</option>
                        </select>
                        <input
                          type="number"
                          min="1"
                          max="1500"
                          value={bulkImportLimit}
                          onChange={(e) => setBulkImportLimit(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleStartBulkImport}
                        disabled={isStartingBulkImport || !bulkImportQuery.trim() || Boolean(bulkImportJob && !['completed', 'failed'].includes(bulkImportJob.status))}
                        className="mt-3 inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isStartingBulkImport ? 'Se pornește...' : 'Pornește importul în masă'}
                      </button>

                      {bulkImportJob && (
                        <div className="mt-3 text-sm text-gray-700">
                          <div className="flex justify-between">
                            <span>Status: {bulkImportJob.status === 'completed' ? 'finalizat' : bulkImportJob.status === 'failed' ? 'eșuat' : 'în desfășurare'}</span>
                            <span>{bulkImportJob.processed}/{bulkImportJob.total || '?'} procesate</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded bg-emerald-100">
                            <div
                              className="h-full bg-emerald-600 transition-all"
                              style={{ width: `${bulkImportJob.total ? Math.round((bulkImportJob.processed / bulkImportJob.total) * 100) : 0}%` }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-gray-600">
                            Importate: {bulkImportJob.imported} · Duplicate: {bulkImportJob.skipped} · Erori: {bulkImportJob.failed}
                          </p>
                        </div>
                      )}
                    </div>

                    {externalError && (
                      <p className="mt-3 text-sm text-red-600">{externalError}</p>
                    )}

                    {externalResults.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {externalResults.map((result) => {
                          const resultKey = `${result.source}:${result.externalId}`;
                          const isImporting = importingExternalId === resultKey;

                          return (
                            <div
                              key={resultKey}
                              className="rounded-md border border-gray-200 bg-white p-4 shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h5 className="text-sm font-semibold text-gray-900">{result.title}</h5>
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                                      {result.source === 'wikisource' ? 'Wikisource' : 'Gutenberg'}
                                    </span>
                                    {result.fileFormat && (
                                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                        {result.fileFormat.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-sm text-gray-700">
                                    {result.author || 'Autor necunoscut'}
                                    {result.language ? ` • ${result.language}` : ''}
                                  </p>
                                  {result.description && (
                                    <p className="mt-2 line-clamp-3 text-sm text-gray-600">
                                      {result.description}
                                    </p>
                                  )}
                                  {result.sourceUrl && (
                                    <a
                                      href={result.sourceUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
                                    >
                                      Vezi sursa
                                    </a>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleExternalImport(result)}
                                  disabled={isImporting}
                                  className="inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isImporting ? 'Se adaugă...' : 'Adaugă în bibliotecă'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Toggle pentru upload singular/multiplu */}
                  <div className="mb-6">
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsMultipleUpload(false);
                          resetForm();
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          !isMultipleUpload
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {t('admin.uploadSingle')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsMultipleUpload(true);
                          resetForm();
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          isMultipleUpload
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                              >
                        {t('admin.uploadMultiple')}
                      </button>
                            </div>
                          </div>

                  {!isMultipleUpload ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                          {t('admin.title')} *
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="title"
                          id="title"
                          required
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={bookData.title}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                          {t('admin.author')} *
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          name="author"
                          id="author"
                          required
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={bookData.author}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                      <div>
                          <label htmlFor="isbn" className="block text-sm font-medium text-gray-700">
                          {t('admin.isbn')} ({t('admin.optional')})
                          </label>
                        <div className="mt-1 space-y-2">
                            <input
                              type="text"
                              name="isbn"
                              id="isbn"
                            placeholder="978-3-16-148410-0"
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              value={isbn}
                            onChange={(e) => setIsbn(e.target.value)}
                            />
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={fetchBookMetadataByISBN}
                              disabled={isLoadingMetadata || !isbn}
                              className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 ${isLoadingMetadata || !isbn ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
                            >
                              {isLoadingMetadata ? t('admin.searching') : 'ISBN'}
                            </button>
                          <button
                            type="button"
                            onClick={fetchBookMetadataByTitleAndAuthor}
                            disabled={isLoadingMetadata || !bookData.title}
                              className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 ${isLoadingMetadata || !bookData.title ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                          >
                              {isLoadingMetadata ? t('admin.searching') : t('admin.searchByTitleAuthor')}
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                          {t('admin.metadataHelp')}
                      </p>
                    </div>

                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          {t('admin.category')} *
                        </label>
                        <div className="mt-1">
                      <select
                        id="category"
                            name="category"
                        value={bookData.category}
                        onChange={(e) => handleCategorySelect(e.target.value)}
                        required
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      >
                            <option value="">{t('admin.selectCategory')}</option>
                        {categories.map((cat) => (
                          <option key={cat.category} value={cat.category}>
                            {cat.category} ({cat.count} cărți)
                          </option>
                        ))}
                            <option value="add-new">+ {t('admin.addNewCategory')}</option>
                      </select>
                      
                      {showNewCategoryInput && (
                            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                              <div className="flex items-center space-x-2">
                          <input
                            type="text"
                                  placeholder={t('admin.newCategoryPlaceholder')}
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddNewCategory()}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                                <button 
                                  type="button" 
                                  onClick={handleAddNewCategory}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  {t('admin.add')}
                                </button>
                                <button 
                                  type="button" 
                                  onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategory('');
                                  }}
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  {t('admin.cancel')}
                                </button>
                              </div>
                        </div>
                      )}
                        </div>
                    </div>

                    <div>
                      <label htmlFor="publishedYear" className="block text-sm font-medium text-gray-700">
                          {t('admin.publishedYear')} ({t('admin.optional')})
                      </label>
                      <div className="mt-1">
                        <input
                          type="number"
                          name="publishedYear"
                          id="publishedYear"
                          min="1000"
                          max={new Date().getFullYear()}
                          className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={bookData.publishedYear}
                          onChange={handleChange}
                        />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          {t('admin.description')} ({t('admin.optional')})
                    </label>
                    <div className="mt-1">
                      <textarea
                        name="description"
                            id="description"
                            rows={4}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        value={bookData.description}
                        onChange={handleChange}
                          />
                    </div>
                  </div>

                  <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('admin.file')} *
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="file-upload"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                              >
                                <span>Încărcați un fișier</span>
                            <input 
                              id="file-upload" 
                                  name="file-upload"
                              type="file" 
                              className="sr-only" 
                              ref={fileInputRef}
                                  onChange={handleFileChange}
                                  accept=".pdf,.epub,.mobi,.txt"
                            />
                          </label>
                              <p className="pl-1">sau trageți și plasați</p>
                        </div>
                            <p className="text-xs text-gray-500">PDF, EPUB, MOBI, TXT până la 50MB</p>
                      </div>
                    </div>
                    {bookData.file && (
                          <p className="mt-1 text-sm text-gray-600">
                            Fișier selectat: {bookData.file.name}
                      </p>
                    )}
                  </div>

                  <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('admin.coverImage')} ({t('admin.optional')})
                    </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                          <div className="space-y-1 text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M24 14l12 12-12 12M8 26h28"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="cover-upload"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                              >
                                <span>Încărcați imagine</span>
                        <input
                                  id="cover-upload"
                                  name="cover-upload"
                          type="file"
                                  className="sr-only"
                          ref={coverImageInputRef}
                          onChange={handleCoverImageChange}
                          accept="image/*"
                        />
                              </label>
                              <p className="pl-1">sau trageți și plasați</p>
                      </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF până la 10MB</p>
                          </div>
                        </div>
                      {coverImagePreview && (
                          <div className="mt-3">
                            <img 
                              src={coverImagePreview} 
                              alt="Preview copertă"
                              className="w-32 h-48 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3">
                            <button
                              type="button"
                          onClick={resetForm}
                          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {t('admin.reset')}
                        </button>
                        <button
                          type="submit"
                          disabled={isUploading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {isUploading ? t('admin.uploading') : t('admin.addBook')}
                            </button>
                          </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      {/* Upload multiplu */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('admin.selectedFiles')}
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                          <div className="space-y-1 text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="multiple-file-upload"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                              >
                                <span>{t('admin.dragDropFiles')}</span>
                                <input
                                  id="multiple-file-upload"
                                  name="multiple-file-upload"
                                  type="file"
                                  multiple
                                  className="sr-only"
                                  ref={multipleFileInputRef}
                                  onChange={handleMultipleFileChange}
                                  accept=".pdf,.epub,.mobi,.txt"
                                />
                              </label>
                        </div>
                            <p className="text-xs text-gray-500">PDF, EPUB, MOBI, TXT până la 50MB fiecare</p>
                    </div>
                  </div>
                      </div>

                      {/* Formular comună pentru toate cărțile */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="common-author" className="block text-sm font-medium text-gray-700">
                            {t('admin.author')} ({t('admin.commonCategory')})
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              name="author"
                              id="common-author"
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              value={bookData.author}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                            {t('admin.category')} ({t('admin.commonCategory')})
                          </label>
                          <div className="mt-1">
                            <select
                              id="category"
                              name="category"
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              value={bookData.category}
                              onChange={handleChange}
                            >
                              <option value="">-- {t('admin.selectCategory')} --</option>
                              {categories.map(cat => (
                                <option key={cat.category} value={cat.category}>
                                  {cat.category} ({cat.count} cărți)
                                </option>
                              ))}
                              {genres.filter(genre => !categories.some(cat => cat.category === genre)).map(genre => (
                                <option key={genre} value={genre}>
                                  {genre}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Lista de fișiere selectate */}
                      {multipleBooks.length > 0 && (
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-3">{t('admin.selectedFiles')}</h3>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {multipleBooks.map((book, index) => (
                              <div key={index} className="border rounded-md p-3 flex items-center justify-between bg-gray-50">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{book.file.name}</p>
                                  <p className="text-xs text-gray-500">{(book.file.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                                <div className="ml-4 flex-shrink-0">
                                  <span 
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                      ${book.status === 'pending' ? 'bg-gray-100 text-gray-800' : 
                                       book.status === 'uploading' ? 'bg-blue-100 text-blue-800' :
                                       book.status === 'success' ? 'bg-green-100 text-green-800' : 
                                       'bg-red-100 text-red-800'}`}
                                  >
                                    {book.message}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                          className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                          {t('admin.reset')}
                </button>
                <button
                          type="button"
                          onClick={handleMultipleUpload}
                          disabled={isUploading || multipleBooks.length === 0}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                          {isUploading ? t('admin.uploading') : t('admin.uploadBooks')}
                </button>
              </div>
                    </div>
                  )}

                  {/* Mesaje de succes/eroare */}
                  {successMessage && (
                    <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                      {successMessage}
                    </div>
                  )}

                  {errorMessage && (
                    <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                      {errorMessage}
                    </div>
                  )}

                  {metadataSuccess && (
                    <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                      {metadataSuccess}
                    </div>
                  )}

                  {metadataError && (
                    <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                      {metadataError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AddBook; 
